import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, TextField, Button, Paper, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { decodeIdToken } from '../utils/token.utils';
import SuperAdminService from '../services/super-admin.service';
import { MigrateUserRequest } from '../types/super-admin';
import AccessDeniedIcon from '@mui/icons-material/NoAccounts';
import SecurityIcon from '@mui/icons-material/Security';

const SuperAdmin: React.FC = () => {
  const decodedIdToken = decodeIdToken();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPasscodeVerified, setIsPasscodeVerified] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasscodeDialog, setOpenPasscodeDialog] = useState(true);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [passcodeAttempts, setPasscodeAttempts] = useState(0);
  const [showUnauthorizedMessage, setShowUnauthorizedMessage] = useState(false);
  const [formData, setFormData] = useState<MigrateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    preferredUsername: '',
    subDomain: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is logged in and has easywebinar email
    if (!decodedIdToken || !decodedIdToken.email) {
      navigate('/logout');
      return;
    }

    const isEasywebinarEmail = decodedIdToken.email.toLowerCase().includes('@easywebinar');
    if (!isEasywebinarEmail) {
      setShowUnauthorizedMessage(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 10000);
      return;
    }

    setIsAuthorized(true);
  }, [decodedIdToken, navigate]);

  const handlePasscodeSubmit = () => {
    if (passcode === 'EW_Super@dm1n#2025!') {
      setIsPasscodeVerified(true);
      setOpenPasscodeDialog(false);
      setPasscodeError('');
      setPasscodeAttempts(0);
    } else {
      setPasscodeAttempts(prev => prev + 1);
      const sarcasticMessages = [
        "Wrong passcode! Did you even try? 🤔",
        "Still wrong! Are you just randomly typing? ⌨️",
        "This is getting embarrassing... 😅",
        "Maybe you should ask someone who knows what they're doing? 🤷‍♂️",
        "This is your last chance before I call security! 🚨"
      ];
      const messageIndex = Math.min(passcodeAttempts, sarcasticMessages.length - 1);
      setPasscodeError(sarcasticMessages[messageIndex]);

      // If this was the last attempt, redirect to dashboard after a short delay
      if (passcodeAttempts >= sarcasticMessages.length - 1) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    }
  };

  const handlePasscodeClose = () => {
    navigate('/dashboard');
  };

  const validateForm = () => {
    if (!isAuthorized || !isPasscodeVerified) {
      navigate('/dashboard');
      return false;
    }

    const newErrors: { [key: string]: string } = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.preferredUsername) {
      newErrors.preferredUsername = 'Preferred username is required';
    }
    
    if (!formData.subDomain) {
      newErrors.subDomain = 'Subdomain is required';
    }

    // Check if either Company ID or Parent Company ID is provided
    if (!formData.companyId && !formData.parentCompanyId) {
      newErrors.companyId = 'Either Company ID or Parent Company ID is required';
      newErrors.parentCompanyId = 'Either Company ID or Parent Company ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isAuthorized || !isPasscodeVerified) {
      navigate('/dashboard');
      return;
    }

    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthorized || !isPasscodeVerified) {
      navigate('/dashboard');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Filter out empty optional fields
      const apiData = {
        ...formData,
        timezone: formData.timezone || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        jobTitle: formData.jobTitle || undefined,
        parentCompanyId: formData.parentCompanyId || undefined,
        companyId: formData.companyId || undefined,
        customDomain: formData.customDomain || undefined,
        profileImage: formData.profileImage || undefined
      };

      const response = await SuperAdminService.migrateUser(apiData);
      setMessage({ type: 'success', text: 'User migrated successfully!' });
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        preferredUsername: '',
        subDomain: '',
      });
      // Close dialog after successful migration
      setTimeout(() => {
        setOpenDialog(false);
        setMessage(null);
        setErrors({});
      }, 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to migrate user. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (!isAuthorized || !isPasscodeVerified) {
      navigate('/dashboard');
      return;
    }

    setOpenDialog(false);
    setMessage(null);
    setErrors({});
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      preferredUsername: '',
      subDomain: '',
    });
  };

  if (showUnauthorizedMessage) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            animation: 'fadeIn 0.5s ease-in',
          }}
        >
          <SecurityIcon sx={{ fontSize: 100, color: 'error.main', mb: 2 }} />
          <AccessDeniedIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom color="error">
            Nice Try! 😏
          </Typography>
          <Typography variant="h5" gutterBottom color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Oh, you thought you could access the Super Admin area? That's cute! 
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Only EasyWebinar administrators are allowed here. 
            Your attempt has been logged and will be reported to the authorities.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting you back to safety in 10 seconds...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      {/* Passcode Dialog */}
      <Dialog 
        open={openPasscodeDialog} 
        onClose={handlePasscodeClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Passcode</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Passcode"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              fullWidth
              error={!!passcodeError}
              helperText={passcodeError}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePasscodeSubmit();
                }
              }}
            />
            {passcodeAttempts > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Attempts: {passcodeAttempts}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasscodeClose}>Cancel</Button>
          <Button 
            onClick={handlePasscodeSubmit}
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {isPasscodeVerified && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Super Admin Dashboard
          </Typography>
          <Typography variant="body1" gutterBottom>
            Welcome to the Super Admin area. This area is restricted to EasyWebinar administrators only.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              size="large"
            >
              Migrate User
            </Button>
          </Box>

          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Migrate User</DialogTitle>
            <DialogContent>
              {message && (
                <Alert severity={message.type} sx={{ mb: 2 }}>
                  {message.text}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
                  <TextField
                    required
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                  <TextField
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                  <TextField
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                  <TextField
                    required
                    label="Preferred Username"
                    name="preferredUsername"
                    value={formData.preferredUsername}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.preferredUsername}
                    helperText={errors.preferredUsername}
                  />
                  <TextField
                    required
                    label="Subdomain"
                    name="subDomain"
                    value={formData.subDomain}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.subDomain}
                    helperText={errors.subDomain || "If subdomain is EXAMPLE.EASYWEBNIAR.LIVE enter EXAMPLE only"}
                  />
                  <TextField
                    label="Timezone"
                    name="timezone"
                    value={formData.timezone || ''}
                    onChange={handleInputChange}
                    fullWidth
                    helperText="Format should be like (GMT+05:30),Asia/Calcutta,IST or (GMT-07:00:00),US/Pacific,PDT"
                  />
                  <TextField
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber || ''}
                    onChange={handleInputChange}
                    fullWidth
                    helperText="Enter the phone number with country code. Example: +919876543210"
                  />
                  <TextField
                    label="Job Title"
                    name="jobTitle"
                    value={formData.jobTitle || ''}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <TextField
                    label="Parent Company ID"
                    name="parentCompanyId"
                    value={formData.parentCompanyId || ''}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.parentCompanyId}
                    helperText={errors.parentCompanyId || "This field is required only for sub accounts. Please use the CHARGEBEE PARENT CUSTOMER ID as parent company Id."}
                  />
                  <TextField
                    label="Company ID"
                    name="companyId"
                    value={formData.companyId || ''}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.companyId}
                    helperText={errors.companyId || "This field is required only for normal users and not for sub accounts. Please use the CHARGEBEE CUSTOMER ID as company Id."}
                  />
                  <TextField
                    label="Custom Domain"
                    name="customDomain"
                    value={formData.customDomain || ''}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <TextField
                    label="Profile Image"
                    name="profileImage"
                    value={formData.profileImage || ''}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </form>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button 
                onClick={handleSubmit}
                variant="contained" 
                color="primary"
                disabled={loading}
              >
                {loading ? 'Migrating...' : 'Migrate User'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Container>
  );
};

export default SuperAdmin; 